import { FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import { CatalogEntityType, ItemUsage } from "@models/catalog.model";
import { FormDialog } from "./FormDialog";
import { UsagePanel } from "./usage";

export function EntityTypeDialog({
  verticalId, entityTypes, usage, initial, onClose, onSaved,
}: {
  verticalId: number;
  entityTypes: CatalogEntityType[]; // parent-type options (same vertical)
  usage?: ItemUsage; // what a code change would break (MPN-1781)
  initial: CatalogEntityType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const svc = new CatalogService();
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [parentId, setParentId] = useState<number | "">(initial?.parent_type_id ?? "");
  const [isReference, setIsReference] = useState(initial?.is_reference ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Entity rows and rule text carry the code as a string with no fan-out, so the server refuses a
  // code change once anything holds it (409); mirror that here so the button matches (MPN-1781).
  const renaming = !!initial && code !== initial.code;
  const blockedReason =
    renaming && usage && usage.tier !== "unreferenced" ? `'${initial.code}' is in use; the code cannot change.` : undefined;

  // A type can't be its own parent; only same-vertical types are valid parents.
  const parentOptions = entityTypes.filter((t) => t.id !== initial?.id);

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      vertical_id: verticalId, code, name,
      parent_type_id: parentId === "" ? null : parentId,
      is_reference: isReference,
    };
    try {
      if (initial) await svc.updateEntityType(initial.id, payload);
      else await svc.createEntityType(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open
      title={initial ? "Edit entity type" : "New entity type"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      canSave={!!code.trim() && !!name.trim() && !blockedReason}
    >
      {/* The code is the `<entity>` half of `<entity>.<attr_key>` in conversion-rule text, so it has to
          lex as one identifier — same failure as `dimension.Enrollment Month` in MPN-1776 (MPN-1782). */}
      <TextField label="Code" value={code}
        onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
        helperText="Lowercase letters, digits and underscores, e.g. customer, purchase. Copied onto every person record and rule; cannot change once in use." size="small" autoFocus />
      {renaming && usage && <UsagePanel usage={usage} blockedReason={blockedReason} />}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)}
        helperText="A label only — always safe to change" size="small" />
      <TextField
        select label="Parent type" value={parentId}
        onChange={(e) => setParentId(e.target.value === "" ? "" : Number(e.target.value))}
        helperText="The 1→many owner (e.g. a purchase's parent is customer). Leave blank for a root type."
        size="small"
      >
        <MenuItem value="">— none (root) —</MenuItem>
        {parentOptions.map((t) => (
          <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Switch checked={isReference} onChange={(e) => setIsReference(e.target.checked)} />}
        label="Reference type (a lookup domain like period/program, not a tracked entity)"
      />
    </FormDialog>
  );
}
